import { Inbox } from 'lucide-react'
import { Badge, Button } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'
import type { AuditLog } from '@/types'
import { format, parseISO } from 'date-fns'
import {
  DataTable,
  DataTableCard,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
} from '@/components/common/DataTable'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { QueryErrorState } from '@/components/common/QueryErrorState'

export interface AuditLogListProps {
  logs: AuditLog[]
  total: number
  page: number
  limit: number
  totalPages: number
  isLoading: boolean
  error: Error | null
  onPageChange: (page: number) => void
  onViewDetail: (log: AuditLog) => void
  onClearFilters?: () => void
  onRetry?: () => void
  hidePagination?: boolean
}

function actionBadgeVariant(action: string): BadgeVariant {
  switch (action.toLowerCase()) {
    case 'create':
    case 'login':
      return 'success'
    case 'delete':
      return 'danger'
    case 'update':
    case 'status_change':
      return 'warning'
    default:
      return 'neutral'
  }
}

function formatTimestamp(iso: string): string {
  try {
    return format(parseISO(iso), 'PPpp')
  } catch {
    return iso
  }
}

export function AuditLogList({
  logs,
  total,
  page,
  limit,
  totalPages,
  isLoading,
  error,
  onPageChange,
  onViewDetail,
  onClearFilters,
  onRetry,
  hidePagination = false,
}: AuditLogListProps): React.ReactElement {
  const safeLogs = Array.isArray(logs) ? logs : []

  if (isLoading) {
    return (
      <DataTableCard>
        <div className="grid grid-cols-7 gap-4 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="h-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="grid grid-cols-7 gap-4 border-b border-gray-200 px-3 py-2 last:border-b-0 dark:border-gray-700"
            aria-hidden
          >
            <div className="h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-28 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-14 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ))}
      </DataTableCard>
    )
  }

  if (error) {
    return (
      <QueryErrorState error={error} title="Couldn't load audit logs" onRetry={onRetry} />
    )
  }

  if (safeLogs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <Inbox className="h-12 w-12 text-gray-400 dark:text-gray-500" aria-hidden />
        <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
          No audit logs
        </h3>
        <p className="max-w-sm text-sm text-gray-600 dark:text-gray-400">
          No audit logs match the current filters.
        </p>
        {onClearFilters && (
          <Button type="button" variant="secondary" onClick={onClearFilters}>
            Clear filters
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <DataTable caption="Audit log entries" captionId="audit-logs-table-caption">
        <DataTableHead sticky>
          <tr>
            <DataTableHeadCell>Time</DataTableHeadCell>
            <DataTableHeadCell>Actor</DataTableHeadCell>
            <DataTableHeadCell>Action</DataTableHeadCell>
            <DataTableHeadCell>Module</DataTableHeadCell>
            <DataTableHeadCell>Entity</DataTableHeadCell>
            <DataTableHeadCell>Financial</DataTableHeadCell>
            <DataTableHeadCell align="right">Actions</DataTableHeadCell>
          </tr>
        </DataTableHead>
        <DataTableBody captionId="audit-logs-table-caption">
          {safeLogs.map((log) => (
            <DataTableRow key={log._id}>
              <DataTableCell className="text-gray-600 dark:text-gray-400">
                {formatTimestamp(log.timestamp)}
              </DataTableCell>
              <DataTableCell>
                <span className="font-medium">{log.actor.name}</span>
                <span className="ml-1 text-gray-500 dark:text-gray-400">
                  ({log.actor.email})
                </span>
              </DataTableCell>
              <DataTableCell>
                <Badge
                  variant={actionBadgeVariant(log.action)}
                  className="px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide"
                >
                  {log.action.replace(/_/g, ' ')}
                </Badge>
              </DataTableCell>
              <DataTableCell>
                <Badge variant="neutral">{log.module}</Badge>
              </DataTableCell>
              <DataTableCell noWrap={false}>
                <span className="font-medium">{log.entity.type}</span>
                {log.entity.description && (
                  <span className="ml-1 text-gray-500 dark:text-gray-400">
                    {log.entity.description}
                  </span>
                )}
              </DataTableCell>
              <DataTableCell>
                {log.isFinancial ? (
                  <Badge variant="info">Financial</Badge>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </DataTableCell>
              <DataTableCell align="right">
                <Button
                  type="button"
                  variant="ghost"
                  className="focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  onClick={() => onViewDetail(log)}
                >
                  View
                </Button>
              </DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>
      {!hidePagination && (
        <TablePaginationBar
          page={page}
          limit={limit}
          total={total}
          totalPages={totalPages}
          onPageChange={onPageChange}
          className="border-t border-gray-200 pt-3 dark:border-gray-700"
        />
      )}
    </div>
  )
}

AuditLogList.displayName = 'AuditLogList'
